// The one markdown renderer: evaluations, the call screen, the thank-you screen.
//
// Renders a documented subset to React elements — never to an HTML string, so
// there is no dangerouslySetInnerHTML anywhere and model output cannot inject
// markup by construction. Anything outside the subset renders as its own literal
// text, which is the honest failure mode: you see the syntax rather than losing
// the sentence.
//
// Supported: ATX headings (#..######), paragraphs, unordered lists (-, *, +),
// ordered lists (1.), blockquotes (>), fenced code blocks (```), horizontal
// rules (---), pipe tables, and inline **bold**, *italic*, `code`, [text](url).
//
// Everything emits the app's type-scale classes rather than styling raw tags, so
// a rendered document looks like the rest of the app (CLAUDE.md § Typography).

import { createElement as h } from 'react'

const HEADING_CLASS = {
  1: 'text-h2 md-h',
  2: 'text-h3 md-h',
  3: 'text-h4 md-h',
  4: 'text-h5 md-h',
  5: 'text-h5 md-h',
  6: 'text-h5 md-h',
}

// --- inline ---------------------------------------------------------------

// Ordered by precedence: code first so `**not bold**` inside backticks stays
// literal, links before emphasis so [**bold label**](url) still links.
const INLINE_RULES = [
  { re: /`([^`]+)`/, render: (m, key) => h('code', { key, className: 'md-code' }, m[1]) },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, key) => {
      const href = m[2]
      // Only http(s) and mailto: a javascript: or data: URL in model output (or
      // in template copy) must never become a live link.
      const safe = /^(https?:|mailto:)/i.test(href)
      return safe
        ? h(
            'a',
            { key, href, className: 'md-link', target: '_blank', rel: 'noopener noreferrer' },
            m[1],
          )
        : `${m[1]} (${href})`
    },
  },
  { re: /\*\*([^*]+)\*\*/, render: (m, key) => h('strong', { key }, m[1]) },
  { re: /__([^_]+)__/, render: (m, key) => h('strong', { key }, m[1]) },
  { re: /(?<![\w*])\*([^*\n]+)\*(?![\w*])/, render: (m, key) => h('em', { key }, m[1]) },
  { re: /(?<![\w_])_([^_\n]+)_(?![\w_])/, render: (m, key) => h('em', { key }, m[1]) },
]

// Text → array of strings and elements. Recursive on the tail so nested spans
// (bold inside a list item inside a table cell) all resolve.
function renderInline(text, keyPrefix = 'i') {
  let earliest = null
  for (const rule of INLINE_RULES) {
    const match = rule.re.exec(text)
    if (match && (earliest === null || match.index < earliest.match.index)) {
      earliest = { rule, match }
    }
  }
  if (!earliest) return [text]
  const { rule, match } = earliest
  const before = text.slice(0, match.index)
  const after = text.slice(match.index + match[0].length)
  const rendered = rule.render(match, `${keyPrefix}-${match.index}`)
  return [
    ...(before ? [before] : []),
    rendered,
    ...renderInline(after, `${keyPrefix}x`),
  ]
}

// --- block ----------------------------------------------------------------

function isBlank(line) {
  return line.trim() === ''
}

function tableRowCells(line) {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return trimmed.split('|').map((cell) => cell.trim())
}

function isTableDivider(line) {
  return /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-')
}

/**
 * Render markdown source to an array of React elements.
 * Returns [] for empty/blank input, so callers can decide what "nothing" looks
 * like rather than getting an empty <div> that collapses the layout.
 */
export function renderMarkdown(source) {
  if (typeof source !== 'string' || !source.trim()) return []
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks = []
  let index = 0
  let key = 0

  while (index < lines.length) {
    const line = lines[index]

    if (isBlank(line)) {
      index += 1
      continue
    }

    // Fenced code — verbatim, no inline parsing inside.
    const fence = line.match(/^\s*```(\w*)\s*$/)
    if (fence) {
      const body = []
      index += 1
      while (index < lines.length && !/^\s*```\s*$/.test(lines[index])) {
        body.push(lines[index])
        index += 1
      }
      index += 1 // closing fence (or EOF — an unclosed fence still renders)
      blocks.push(
        h(
          'pre',
          { key: key++, className: 'md-pre' },
          h('code', null, body.join('\n')),
        ),
      )
      continue
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(h('hr', { key: key++, className: 'md-hr' }))
      index += 1
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      const level = heading[1].length
      blocks.push(
        h(
          `h${Math.min(level + 1, 6)}`,
          { key: key++, className: HEADING_CLASS[level] },
          renderInline(heading[2].trim(), `h${key}`),
        ),
      )
      index += 1
      continue
    }

    if (/^\s*>\s?/.test(line)) {
      const body = []
      while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
        body.push(lines[index].replace(/^\s*>\s?/, ''))
        index += 1
      }
      blocks.push(
        h(
          'blockquote',
          { key: key++, className: 'md-quote' },
          ...renderMarkdown(body.join('\n')),
        ),
      )
      continue
    }

    // Table: a header row followed by a divider row.
    if (line.includes('|') && index + 1 < lines.length && isTableDivider(lines[index + 1])) {
      const header = tableRowCells(line)
      index += 2
      const rows = []
      while (index < lines.length && lines[index].includes('|') && !isBlank(lines[index])) {
        rows.push(tableRowCells(lines[index]))
        index += 1
      }
      blocks.push(
        h(
          'div',
          { key: key++, className: 'md-table-wrap' },
          h('table', { className: 'md-table' }, [
            h(
              'thead',
              { key: 'head' },
              h(
                'tr',
                null,
                header.map((cell, i) => h('th', { key: i }, renderInline(cell, `th${i}`))),
              ),
            ),
            h(
              'tbody',
              { key: 'body' },
              rows.map((row, r) =>
                h(
                  'tr',
                  { key: r },
                  row.map((cell, c) => h('td', { key: c }, renderInline(cell, `td${r}-${c}`))),
                ),
              ),
            ),
          ]),
        ),
      )
      continue
    }

    const bullet = line.match(/^\s*([-*+])\s+(.*)$/)
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/)
    if (bullet || numbered) {
      const ordered = Boolean(numbered)
      const items = []
      while (index < lines.length) {
        const current = lines[index]
        const match = ordered
          ? current.match(/^\s*(\d+)[.)]\s+(.*)$/)
          : current.match(/^\s*([-*+])\s+(.*)$/)
        if (!match) {
          // A wrapped continuation line belongs to the item above it.
          if (!isBlank(current) && /^\s{2,}\S/.test(current) && items.length > 0) {
            items[items.length - 1] += ` ${current.trim()}`
            index += 1
            continue
          }
          break
        }
        items.push(match[2])
        index += 1
      }
      blocks.push(
        h(
          ordered ? 'ol' : 'ul',
          { key: key++, className: ordered ? 'md-ol' : 'md-ul' },
          items.map((item, i) => h('li', { key: i }, renderInline(item, `li${i}`))),
        ),
      )
      continue
    }

    // Paragraph: everything up to the next blank line or block starter.
    const paragraph = []
    while (index < lines.length && !isBlank(lines[index])) {
      const current = lines[index]
      if (
        /^(#{1,6})\s+/.test(current) ||
        /^\s*>\s?/.test(current) ||
        /^\s*([-*+])\s+/.test(current) ||
        /^\s*\d+[.)]\s+/.test(current) ||
        /^\s*```/.test(current) ||
        /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(current)
      ) {
        break
      }
      paragraph.push(current.trim())
      index += 1
    }
    if (paragraph.length > 0) {
      blocks.push(
        h(
          'p',
          { key: key++, className: 'text-body-md md-p' },
          renderInline(paragraph.join(' '), `p${key}`),
        ),
      )
    }
  }

  return blocks
}

/**
 * The renderer as a component. `source` is markdown; `className` lands on the
 * wrapper so callers control spacing.
 */
export default function Markdown({ source, className = '' }) {
  const blocks = renderMarkdown(source)
  if (blocks.length === 0) return null
  return h('div', { className: `markdown ${className}`.trim() }, blocks)
}
